import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, Typography, Box, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tabs, Tab, Chip, CircularProgress, Alert } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Check as ApproveIcon, People as PeopleIcon, Payment as PaymentIcon, VpnKey as VpnKeyIcon, Api as ApiIcon, OnlinePrediction as OnlineIcon, SignalWifiOff as OfflineIcon, DateRange as DateRangeIcon, Info as InfoIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import api from '../services/api';
import UserModal from './UserModal';
import PixKeyModal from './PixKeyModal';
import TokenModal from './TokenModal';

const Dashboard = () => {
  const [data, setData] = useState({
    activeUsers: 0,
    inactiveUsers: 0,
    creditPayments: 0,
    pixPayments: 0,
    pixKeys: 0,
    tokens: 0,
    onlineUsers: 0,
    offlineUsers: 0,
    dateTime: new Date().toLocaleString(),
    recentUsers: [],
    recentPayments: [],
    pixKeysList: [],
    tokensList: []
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [pixKeyModalOpen, setPixKeyModalOpen] = useState(false);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPixKey, setCurrentPixKey] = useState(null);
  const [currentToken, setCurrentToken] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      const usersResponse = await api.get('/users');
      const paymentsResponse = await api.get('/payments_user');
      const pixKeysResponse = await api.get('/bank_data');
      const tokensResponse = await api.get('/tk_credenci');
      const recentUsers = usersResponse.data.users.sort((a, b) => b.id - a.id).slice(0, 4);
      const recentPayments = paymentsResponse.data.payments.slice(0, 4);
      const recentPixKeys = pixKeysResponse.data.sort((a, b) => b.id - a.id).slice(0, 4);
      const recentTokens = tokensResponse.data.sort((a, b) => b.id - a.id).slice(0, 4);

      setData({
        ...response.data,
        dateTime: new Date().toLocaleString(),
        recentUsers: recentUsers,
        recentPayments: recentPayments,
        pixKeysList: recentPixKeys,
        tokensList: recentTokens
      });

      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
      setError('Failed to fetch dashboard data');
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setCurrentUser(user);
    setModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    Swal.fire({
      title: 'Você tem certeza?',
      text: `Deseja realmente excluir o usuário ${user.username}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir!',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/users/delete-user/${user.id}`);
          setData((prevData) => ({
            ...prevData,
            recentUsers: prevData.recentUsers.filter((u) => u.id !== user.id)
          }));
          Swal.fire('Excluído!', 'O usuário foi excluído.', 'success');
          fetchData();
        } catch (error) {
          Swal.fire('Erro!', 'Falha ao excluir o usuário.', 'error');
        }
      }
    });
  };

  const handleAddUser = () => {
    setCurrentUser(null);
    setModalOpen(true);
  };

  const handleSaveUser = async (user) => {
    try {
      if (user.id) {
        await api.put(`/users/${user.id}`, user);
        setData((prevData) => ({
          ...prevData,
          recentUsers: prevData.recentUsers.map((u) => (u.id === user.id ? user : u))
        }));
      } else {
        const response = await api.post('/users', user);
        setData((prevData) => ({
          ...prevData,
          recentUsers: [response.data, ...prevData.recentUsers].slice(0, 4)
        }));
      }
      setModalOpen(false);
      Swal.fire('Sucesso!', 'Usuário salvo com sucesso.', 'success');
      fetchData();
    } catch (error) {
      Swal.fire('Erro!', 'Falha ao salvar o usuário.', 'error');
    }
  };

  const handleApprovePayment = async (payment) => {
    try {
      const userId = payment.id_user;
      const user = data.recentUsers.find((u) => u.id === userId);
      if (user && user.status === 0) {
        await api.put(`/users/update-status/${userId}`, { status: 1 });
        setData((prevData) => ({
          ...prevData,
          recentUsers: prevData.recentUsers.map((u) => (u.id === userId ? { ...u, status: 1 } : u)),
          recentPayments: prevData.recentPayments.map((p) => (p.id === payment.id ? { ...p, status: 'approved' } : p))
        }));
        Swal.fire('Sucesso!', 'Pagamento aprovado e status do usuário atualizado com sucesso.', 'success');
      } else {
        Swal.fire('Aviso!', 'Usuário já está ativo.', 'info');
      }
    } catch (error) {
      Swal.fire('Erro!', 'Falha ao aprovar o pagamento e atualizar o status do usuário.', 'error');
    }
  };

  const handleAddPixKey = () => {
    setCurrentPixKey(null);
    setPixKeyModalOpen(true);
  };

  const handleEditPixKey = (pixKey) => {
    setCurrentPixKey(pixKey);
    setPixKeyModalOpen(true);
  };

  const handleDeletePixKey = (pixKey) => {
    Swal.fire({
      title: 'Você tem certeza?',
      text: `Deseja realmente excluir a chave pix ${pixKey.chavepix}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir!',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/bank_data/delete-bank/${pixKey.id}`);
          setData((prevData) => ({
            ...prevData,
            pixKeysList: prevData.pixKeysList.filter((p) => p.id !== pixKey.id)
          }));
          Swal.fire('Excluído!', 'A chave pix foi excluída.', 'success');
          fetchData();
        } catch (error) {
          Swal.fire('Erro!', 'Falha ao excluir a chave pix.', 'error');
        }
      }
    });
  };

  const handleSavePixKey = async (pixKey) => {
    try {
      if (pixKey.id) {
        await api.put(`/bank_data/update-bkd-nome/${pixKey.id}`, { bkdnome: pixKey.nome });
        await api.put(`/bank_data/update-chavepix/${pixKey.id}`, { chavepix: pixKey.chavepix });
        await api.put(`/bank_data/update-valor/${pixKey.id}`, { valor: pixKey.valor });
        await api.put(`/bank_data/update-city/${pixKey.id}`, { cidade: pixKey.cidade });
        setData((prevData) => ({
          ...prevData,
          pixKeysList: prevData.pixKeysList.map((p) => (p.id === pixKey.id ? pixKey : p))
        }));
      } else {
        const response = await api.post('/bank_data/create-bank', pixKey);
        setData((prevData) => ({
          ...prevData,
          pixKeysList: [response.data, ...prevData.pixKeysList].slice(0, 4)
        }));
      }
      setPixKeyModalOpen(false);
      Swal.fire('Sucesso!', 'Chave Pix salva com sucesso.', 'success');
      fetchData();
    } catch (error) {
      Swal.fire('Erro!', 'Falha ao salvar a chave Pix.', 'error');
    }
  };

  const handleAddToken = () => {
    setCurrentToken(null);
    setTokenModalOpen(true);
  };

  const handleEditToken = (token) => {
    setCurrentToken(token);
    setTokenModalOpen(true);
  };

  const handleDeleteToken = (token) => {
    Swal.fire({
      title: 'Você tem certeza?',
      text: `Deseja realmente excluir o token ${token.tk}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir!',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/tk_credenci/delete-tk-credenci/${token.id}`);
          setData((prevData) => ({
            ...prevData,
            tokensList: prevData.tokensList.filter((t) => t.id !== token.id)
          }));
          Swal.fire('Excluído!', 'O token foi excluído.', 'success');
          fetchData();
        } catch (error) {
          Swal.fire('Erro!', 'Falha ao excluir o token.', 'error');
        }
      }
    });
  };

  const handleSaveToken = async (token) => {
    try {
      if (token.id) {
        await api.put(`/tk_credenci/update-tk-credenci/${token.id}`, { tk: token.tk });
        await api.put(`/tk_credenci/update-origin-credenci/${token.id}`, { origin: token.origin });
        await api.put(`/tk_credenci/update-type-credenci/${token.id}`, { type: token.type });
        setData((prevData) => ({
          ...prevData,
          tokensList: prevData.tokensList.map((t) => (t.id === token.id ? token : t))
        }));
      } else {
        const response = await api.post('/tk_credenci/create-tk-credenci', token);
        setData((prevData) => ({
          ...prevData,
          tokensList: [response.data, ...prevData.tokensList].slice(0, 4)
        }));
      }
      setTokenModalOpen(false);
      Swal.fire('Sucesso!', 'Token salvo com sucesso.', 'success');
      fetchData();
    } catch (error) {
      Swal.fire('Erro!', 'Falha ao salvar o token.', 'error');
    }
  };

  const getStatusLabel = (status) => {
    return status === 1 ? 'Ativo' : 'Inativo';
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box className="dashboard-container" sx={{ mt: 3, ml: 8 }}>
      <Container className="dashboard-content">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ minHeight: '150px', maxHeight: '150px', bgcolor: 'lightblue', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <PeopleIcon sx={{ fontSize: 40 }} />
                <Typography variant="h5" gutterBottom>Usuários</Typography>
                <Typography>Ativos: {data.activeUsers}</Typography>
                <Typography>Inativos: {data.inactiveUsers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ minHeight: '150px', maxHeight: '150px', bgcolor: 'lightgreen', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <PaymentIcon sx={{ fontSize: 40 }} />
                <Typography variant="h5" gutterBottom>Pagamentos</Typography>
                <Typography>Cartão de Crédito: {data.creditPayments}</Typography>
                <Typography>Pix: {data.pixPayments}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ minHeight: '150px', maxHeight: '150px', bgcolor: 'lightcoral', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <VpnKeyIcon sx={{ fontSize: 40 }} />
                <Typography variant="h5" gutterBottom>Chave Pix</Typography>
                <Typography>Registrada: {data.pixKeys}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ minHeight: '150px', maxHeight: '150px', bgcolor: 'lightgoldenrodyellow', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <ApiIcon sx={{ fontSize: 40 }} />
                <Typography variant="h5" gutterBottom>Tokens API</Typography>
                <Typography>Quantidade: {data.tokens}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ minHeight: '150px', maxHeight: '150px', bgcolor: 'lightblue', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <OnlineIcon sx={{ fontSize: 40 }} />
                <Typography variant="h5" gutterBottom>Usuários Online</Typography>
                <Typography>Online: {data.onlineUsers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ minHeight: '150px', maxHeight: '150px', bgcolor: 'lightcoral', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <OfflineIcon sx={{ fontSize: 40 }} />
                <Typography variant="h5" gutterBottom>Usuários Offline</Typography>
                <Typography>Offline: {data.offlineUsers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ minHeight: '150px', maxHeight: '150px', bgcolor: 'lightgoldenrodyellow', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <DateRangeIcon sx={{ fontSize: 40 }} />
                <Typography variant="h5" gutterBottom>Data</Typography>
                <Typography>{data.dateTime}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ minHeight: '150px', maxHeight: '150px', bgcolor: 'lightgray', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <InfoIcon sx={{ fontSize: 40 }} />
                <Typography variant="h5" gutterBottom>Card Modelo</Typography>
                <Typography>Descrição</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: 3 }}>
              <CardContent>
                <Tabs value={selectedTab} onChange={handleTabChange} variant="fullWidth" sx={{ justifyContent: 'flex-start' }}>
                  <Tab label="Usuários Recentes" />
                  <Tab label="Pagamentos" />
                  <Tab label="Chave Pix" />
                  <Tab label="Tokens" />
                </Tabs>
                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
                  {selectedTab === 0 && (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', width: '80%' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell align="center">ID</TableCell>
                            <TableCell align="center">Username</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">
                              Actions
                              <IconButton color="primary" aria-label="add user" onClick={handleAddUser} style={{ marginLeft: '10px' }}>
                                <AddIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.recentUsers.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell align="center">{user.id}</TableCell>
                              <TableCell align="center">{user.username}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={getStatusLabel(user.status)}
                                  sx={{
                                    width: 80,
                                    bgcolor: user.status === 1 ? 'green' : 'red',
                                    color: 'white'
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <IconButton color="primary" aria-label="edit user" onClick={() => handleEditUser(user)}>
                                  <EditIcon />
                                </IconButton>
                                <IconButton color="secondary" aria-label="delete user" onClick={() => handleDeleteUser(user)} style={{ marginLeft: '10px' }}>
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {selectedTab === 1 && (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', width: '80%' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell align="center">ID</TableCell>
                            <TableCell align="center">ID Usuário</TableCell>
                            <TableCell align="center">txtId</TableCell>
                            <TableCell align="center">
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.recentPayments.map((payment) => (
                            <TableRow key={payment.id}>
                              <TableCell align="center">{payment.id}</TableCell>
                              <TableCell align="center">{payment.id_user}</TableCell>
                              <TableCell align="center">{payment.txtId}</TableCell>
                              <TableCell align="center">
                                <IconButton color="primary" aria-label="approve payment" onClick={() => handleApprovePayment(payment)}>
                                  <ApproveIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {selectedTab === 2 && (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', width: '80%' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell align="center">ID</TableCell>
                            <TableCell align="center">Nome</TableCell>
                            <TableCell align="center">Chave</TableCell>
                            <TableCell align="center">Valor</TableCell>
                            <TableCell align="center">Cidade</TableCell>
                            <TableCell align="center">
                              Actions
                              <IconButton color="primary" aria-label="add pix key" onClick={handleAddPixKey} style={{ marginLeft: '10px' }}>
                                <AddIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.pixKeysList.map((pixKey) => (
                            <TableRow key={pixKey.id}>
                              <TableCell align="center">{pixKey.id}</TableCell>
                              <TableCell align="center">{pixKey.nome}</TableCell>
                              <TableCell align="center">{pixKey.chavepix}</TableCell>
                              <TableCell align="center">{formatCurrency(pixKey.valor)}</TableCell>
                              <TableCell align="center">{pixKey.cidade}</TableCell>
                              <TableCell align="center">
                                <IconButton color="primary" aria-label="edit pix key" onClick={() => handleEditPixKey(pixKey)}>
                                  <EditIcon />
                                </IconButton>
                                <IconButton color="secondary" aria-label="delete pix key" onClick={() => handleDeletePixKey(pixKey)} style={{ marginLeft: '10px' }}>
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {selectedTab === 3 && (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', width: '90%' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell align="center">ID</TableCell>
                            <TableCell align="center">Token</TableCell>
                            <TableCell align="center">Descrição</TableCell>
                            <TableCell align="center">Tipo</TableCell>
                            <TableCell align="center">
                              Actions
                              <IconButton color="primary" aria-label="add token" onClick={handleAddToken} style={{ marginLeft: '10px' }}>
                                <AddIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.tokensList.map((token) => (
                            <TableRow key={token.id}>
                              <TableCell align="center">{token.id}</TableCell>
                              <TableCell align="center">{token.tk}</TableCell>
                              <TableCell align="center">{token.origin}</TableCell>
                              <TableCell align="center">{token.type}</TableCell>
                              <TableCell align="center">
                                <IconButton color="primary" aria-label="edit token" onClick={() => handleEditToken(token)}>
                                  <EditIcon />
                                </IconButton>
                                <IconButton color="secondary" aria-label="delete token" onClick={() => handleDeleteToken(token)} style={{ marginLeft: '10px' }}>
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <UserModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSaveUser} user={currentUser} fetchUsers={fetchData} />
      <PixKeyModal open={pixKeyModalOpen} onClose={() => setPixKeyModalOpen(false)} onSave={handleSavePixKey} pixKey={currentPixKey} fetchData={fetchData} />
      <TokenModal open={tokenModalOpen} onClose={() => setTokenModalOpen(false)} onSave={handleSaveToken} token={currentToken} fetchData={fetchData} />
    </Box>
  );
};

export default Dashboard;
